/**
 * Web Audio API based sound synthesizer for incoming tickets and messages.
 * Uses zero external asset dependencies, works offline, and produces a
 * pleasant two-tone chime (D5 -> A5) similar to modern helpdesk software.
 */

let audioCtx = null;
let fallbackAudioUrl = '';

/** Purpose: synthesize the two-note notification tone in Web Audio. Why: avoids a hosted audio asset and works offline when browser audio is unlocked. */
const scheduleChime = (ctx) => {
  const now = ctx.currentTime;

  // First chime note: D5 (587.33 Hz)
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(587.33, now);
  gain1.gain.setValueAtTime(0.4, now);
  gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(now);
  osc1.stop(now + 0.35);

  // Second chime note: A5 (880 Hz) - clear arrival tone
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(880, now + 0.14);
  gain2.gain.setValueAtTime(0.45, now + 0.14);
  gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.65);
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(now + 0.14);
  osc2.stop(now + 0.65);
};

// A short audible WAV for browsers where Web Audio is unavailable or cannot resume.
/** Purpose: lazily encode a short WAV fallback as a data URL. Why: some browsers cannot resume Web Audio, so an Audio element gets a second playback path. */
const getFallbackAudioUrl = () => {
  if (fallbackAudioUrl || typeof window === 'undefined' || typeof window.btoa !== 'function') {
    return fallbackAudioUrl;
  }

  const sampleRate = 8000;
  const sampleCount = Math.round(sampleRate * 0.68);
  const bytes = new Uint8Array(44 + sampleCount);
  const view = new DataView(bytes.buffer);
  // Purpose: write WAV chunk identifiers into the byte buffer. Why: RIFF/WAVE headers require literal ASCII markers at fixed offsets.
  const writeText = (offset, value) => {
    for (let i = 0; i < value.length; i += 1) bytes[offset + i] = value.charCodeAt(i);
  };

  writeText(0, 'RIFF');
  view.setUint32(4, 36 + sampleCount, true);
  writeText(8, 'WAVE');
  writeText(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate, true);
  view.setUint16(32, 1, true);
  view.setUint16(34, 8, true);
  writeText(36, 'data');
  view.setUint32(40, sampleCount, true);

  for (let i = 0; i < sampleCount; i += 1) {
    const time = i / sampleRate;
    const firstEnvelope = Math.max(0, 1 - time / 0.35);
    const secondTime = time - 0.14;
    const secondEnvelope = secondTime >= 0 ? Math.max(0, 1 - secondTime / 0.51) : 0;
    const sample = (Math.sin(2 * Math.PI * 587.33 * time) * firstEnvelope
      + Math.sin(2 * Math.PI * 880 * secondTime) * secondEnvelope * (secondTime >= 0 ? 1 : 0)) * 0.32;
    bytes[44 + i] = Math.max(0, Math.min(255, Math.round(128 + sample * 127)));
  }

  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  fallbackAudioUrl = `data:audio/wav;base64,${window.btoa(binary)}`;
  return fallbackAudioUrl;
};

/** Purpose: play the generated WAV through the browser Audio element. Why: provides a fallback if Web Audio is missing or blocked. */
const playFallbackChime = async () => {
  try {
    if (typeof Audio !== 'undefined') {
      const audio = new Audio(getFallbackAudioUrl());
      audio.volume = 0.8;
      await audio.play();
      return true;
    }
  } catch {}
  return false;
};

// Initialize or resume the shared audio context
/** Purpose: create or reuse a live Web Audio context. Why: a shared context avoids recreating browser audio resources for every notification. */
export const getAudioContext = () => {
  try {
    const AudioContextClass = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
    if (!AudioContextClass) return null;

    if (!audioCtx || audioCtx.state === 'closed') {
      audioCtx = new AudioContextClass();
    }
    return audioCtx;
  } catch {
    return null;
  }
};

// Resume audio directly inside a real user gesture. Browsers block sound from
// socket events until the page has received a gesture; doing this on capture
// also ensures React handlers cannot run before the unlock attempt.
if (typeof window !== 'undefined') {
  // Purpose: resume audio after a user gesture. Why: browser autoplay policies block sound triggered later by sockets until a gesture unlocks playback.
  const unlockAudio = () => {
    try {
      const ctx = getAudioContext();
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
    } catch {}
  };

  ['click', 'keydown', 'touchstart', 'pointerdown'].forEach((evt) => {
    window.addEventListener(evt, unlockAudio, { passive: true, capture: true });
  });
}

/** Purpose: play the incoming-ticket chime and report whether playback succeeded. Why: Inbox can alert on new assignments while accurately handling browser audio restrictions. */
export const playTicketChime = async () => {
  let ctx;
  try {
    ctx = getAudioContext();
    if (ctx) {
      // Awaiting resume is essential: scheduling while suspended can silently
      // queue a chime against a frozen audio clock (especially after tab idle).
      if (ctx.state !== 'running') await ctx.resume();
      if (ctx.state === 'running') {
        scheduleChime(ctx);
        return true;
      }
    }
  } catch {}

  // Safari/Chrome may still reject Web Audio after backgrounding or a policy
  // change. Try a real WAV Audio element and report failure to the UI if that
  // is blocked too, instead of claiming the test succeeded unconditionally.
  return playFallbackChime();
};
